/**
 * @file patiosService.js
 * @description Servicio de gestión de patios (sitios) y subsitios
 * @module services/patiosService
 * 
 * Funcionalidades principales:
 * - CRUD de patios/sitios (Create, Read, Update, Delete)
 * - Obtención de subsitios por patio
 * - Filtrado de sitios por estado
 * - Obtención de todos los estados con estadísticas
 * - Hooks personalizados: usePatios, useSubsitiosPatio
 * - Mapeo de vehículos a sitios/subsitios
 */

import axios from './authService';
import { useState, useEffect } from 'react';

/**
 * Obtiene todos los patios/sitios del sistema
 * 
 * @async
 * @function fetchPatios
 * @returns {Promise<Object>} Resultado de la operación
 * @property {boolean} success - true si la operación fue exitosa
 * @property {Array} data - Array de patios
 * @property {string} [message] - Mensaje de error si falla
 * @property {number} [statusCode] - Código HTTP de error
 */
export const fetchPatios = async () => {
  try {
    const response = await axios.get('/api/patios/');
    const patiosData = response.data.results || response.data || [];

    return {
      success: true,
      data: Array.isArray(patiosData) ? patiosData : []
    };
  } catch (error) {
    console.error('Error al obtener sitios:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.detail || 'Error al obtener sitios',
      statusCode: error.response?.status,
      data: []
    };
  }
};

/**
 * Obtiene todos los subsitios de un patio específico con sus vehículos
 * 
 * Proceso:
 * 1. Busca el patio por ID
 * 2. Filtra vehículos que pertenecen a cada subsitio
 * 3. Calcula estadísticas (ocupación, marcas, etc.)
 * 4. Ordena subsitios (con vehículos primero)
 * 
 * @async
 * @function fetchAllSubsitiosFromPatio
 * @param {number|string} patioId - ID del patio
 * @param {Array} [vehiculosExistentes=[]] - Array de vehículos para filtrar
 * @returns {Promise<Object>} Resultado con subsitios y datos del patio
 * @property {boolean} success - true si fue exitoso
 * @property {Array} data - Array de subsitios con estadísticas
 * @property {Object} patio - Información del patio padre
 */
export const fetchAllSubsitiosFromPatio = async (patioId, vehiculosExistentes = []) => {
  try {
    const patiosResult = await fetchPatios();
    if (!patiosResult.success) {
      return patiosResult;
    }

    // Encontrar el patio específico
    const patio = patiosResult.data.find(p =>
      p.id === patioId ||
      p.nombre === patioId ||
      p.id?.toString() === patioId?.toString()
    );

    if (!patio) {
      return {
        success: false,
        message: 'Patio no encontrado',
        data: []
      };
    }

    const vehiculosData = vehiculosExistentes || [];
    const subsitiosDefinidos = patio.subpatios || patio.subpatios_data || [];

    // Si el patio no tiene subsitios definidos
    if (subsitiosDefinidos.length === 0) {
      return {
        success: true,
        data: [],
        patio: {
          id: patio.id,
          nombre: patio.nombre,
          estado: patio.estado,
          ubicacion: patio.ubicacion,
          capacidad: patio.capacidad
        }
      };
    }

    // Procesar cada subsitio
    const todosLosSubsitios = subsitiosDefinidos.map(subsitio => {
      // Filtrar vehículos que pertenecen a este subsitio
      const vehiculosSubsitio = vehiculosData.filter(vehiculo => {
        // Verificar lectura RFID válida
        if (!vehiculo.lectura_rfid) return false;

        const vehiculoSubsitio = vehiculo.lectura_rfid.subpatio_nombre || '';
        const vehiculoPatio = vehiculo.lectura_rfid.patio_nombre || '';
        const matchSubsitio = vehiculoSubsitio.toLowerCase() === subsitio.nombre.toLowerCase();
        const matchPatio = vehiculoPatio.toLowerCase() === patio.nombre.toLowerCase();

        return matchSubsitio && matchPatio;
      });

      // Contar vehículos por marca
      const marcasCounts = {};
      vehiculosSubsitio.forEach(vehiculo => {
        const marca = vehiculo.marca || vehiculo.Marca || vehiculo.brand || 'DESCONOCIDA';
        marcasCounts[marca] = (marcasCounts[marca] || 0) + 1;
      });

      // Crear objeto subsitio completo
      return {
        id: subsitio.id || `${patio.id}-${subsitio.nombre}`,
        nombre: subsitio.nombre,
        sitio: patio.nombre,
        sitioId: patio.id,
        capacidad: subsitio.capacidad || 0,
        descripcion: subsitio.descripcion || '',
        totalVehiculos: vehiculosSubsitio.length,
        marcas: marcasCounts,
        vehiculos: vehiculosSubsitio,
        estado: patio.estado,
        ubicacion: patio.ubicacion,
        categoria: patio.categoria,
        estaVacio: vehiculosSubsitio.length === 0,
        porcentajeOcupacion: subsitio.capacidad > 0 ? (vehiculosSubsitio.length / subsitio.capacidad) * 100 : 0
      };
    });

    // Ordenar: subsitios con vehículos primero, luego alfabéticamente
    todosLosSubsitios.sort((a, b) => {
      if (a.estaVacio && !b.estaVacio) return 1;
      if (!a.estaVacio && b.estaVacio) return -1;
      return a.nombre.localeCompare(b.nombre);
    });

    return {
      success: true,
      data: todosLosSubsitios,
      patio: {
        id: patio.id,
        nombre: patio.nombre,
        estado: patio.estado,
        ubicacion: patio.ubicacion,
        capacidad: patio.capacidad
      }
    };

  } catch (error) {
    console.error('Error al obtener todos los subsitios del patio:', error);
    return {
      success: false,
      message: error.response?.data?.detail || 'Error al obtener subsitios del patio',
      statusCode: error.response?.status,
      data: []
    };
  }
};

/**
 * Obtiene todos los subsitios del sistema (de todos los patios)
 * DEPRECATED: Esta función carga todos los vehículos, considerar usar fetchAllSubsitiosFromPatio
 * 
 * @async
 * @function fetchAllSubsitios
 * @returns {Promise<Object>} Resultado con todos los subsitios
 * @property {boolean} success - true si fue exitoso
 * @property {Array} data - Array de todos los subsitios
 */
export const fetchAllSubsitios = async () => {
  try {
    const patiosResult = await fetchPatios();
    if (!patiosResult.success) {
      return patiosResult;
    }

    // Intentar cargar vehículos
    let vehiculosData = [];
    try {
      const vehiclesResponse = await axios.get('/vehiculos/');
      vehiculosData = vehiclesResponse.data.results || vehiclesResponse.data || [];
    } catch (error) {
      console.warn('No se pudieron cargar los vehículos:', error.message);
    }

    const todosLosSubsitios = [];

    // Procesar cada patio y sus subsitios
    for (const patio of patiosResult.data) {
      const subsitiosDefinidos = patio.subsitios || patio.subpatios_data || [];

      subsitiosDefinidos.forEach(subsitio => {
        // Filtrar vehículos para este subsitio
        const vehiculosSubsitio = vehiculosData.filter(vehiculo => {
          const vehiculoSubsitio = vehiculo.subsitio || vehiculo.patio_subsitio || vehiculo.ubicacion_subsitio || '';
          const vehiculoPatio = vehiculo.patio || vehiculo.sitio || vehiculo.ubicacion_patio || '';

          return (vehiculoSubsitio.toLowerCase() === subsitio.nombre.toLowerCase()) &&
            (vehiculoPatio.toLowerCase() === patio.nombre.toLowerCase() || vehiculo.patio_id === patio.id);
        });

        // Contar por marcas
        const marcasCounts = {};
        vehiculosSubsitio.forEach(vehiculo => {
          const marca = vehiculo.marca || vehiculo.brand || 'DESCONOCIDA';
          marcasCounts[marca] = (marcasCounts[marca] || 0) + 1;
        });

        // Agregar subsitio procesado
        todosLosSubsitios.push({
          id: subsitio.id || `${patio.id}-${subsitio.nombre}`,
          nombre: subsitio.nombre,
          sitio: patio.nombre,
          sitioId: patio.id,
          capacidad: subsitio.capacidad || 0,
          descripcion: subsitio.descripcion || '',
          totalVehiculos: vehiculosSubsitio.length,
          marcas: marcasCounts,
          vehiculos: vehiculosSubsitio,
          estado: patio.estado,
          ubicacion: patio.ubicacion,
          categoria: patio.categoria,
          estaVacio: vehiculosSubsitio.length === 0,
          porcentajeOcupacion: subsitio.capacidad > 0 ? (vehiculosSubsitio.length / subsitio.capacidad) * 100 : 0
        });
      });
    }

    return {
      success: true,
      data: todosLosSubsitios
    };

  } catch (error) {
    console.error('Error al obtener todos los subsitios:', error);
    return {
      success: false,
      message: error.response?.data?.detail || 'Error al obtener todos los subsitios',
      statusCode: error.response?.status,
      data: []
    };
  }
};

/**
 * Obtiene todos los sitios/patios de un estado específico
 * 
 * Filtra patios por estado y calcula estadísticas de vehículos
 *
 * @async
 * @function fetchAllSitiosByEstado
 * @param {string} estadoNombre - Nombre del estado (ej: 'Yucatán')
 * @param {Array} [vehiculosExistentes=[]] - Array de vehículos para filtrar
 * @returns {Promise<Object>} Resultado con sitios del estado
 * @property {boolean} success - true si fue exitoso
 * @property {Array} data - Array de sitios con estadísticas
 */
export const fetchAllSitiosByEstado = async (estadoNombre, vehiculosExistentes = []) => {
  try {
    const patiosResult = await fetchPatios();
    if (!patiosResult.success) {
      return patiosResult;
    }

    // Filtrar patios por estado
    const patiosDelEstado = patiosResult.data.filter(patio =>
      patio.estado === estadoNombre
    );

    if (patiosDelEstado.length === 0) {
      return {
        success: true,
        data: [],
        message: 'No se encontraron sitios para este estado'
      };
    }

    const vehiculosData = vehiculosExistentes || [];

    // Procesar cada patio
    const todosLosSitios = patiosDelEstado.map(patio => {
      // Filtrar vehículos en patio (antenna_id 1 o null)
      const vehiculosPatio = vehiculosData.filter(vehiculo => {
        const vehiculoPatio = vehiculo.lectura_rfid?.patio_nombre || '';
        const matchPatio = vehiculoPatio.toLowerCase() === patio.nombre.toLowerCase();
        return matchPatio;
      });

      // Contar por marca
      const marcasCounts = {};
      vehiculosPatio.forEach(vehiculo => {
        const marca = vehiculo.marca || vehiculo.Marca || vehiculo.brand || 'DESCONOCIDA';
        marcasCounts[marca] = (marcasCounts[marca] || 0) + 1;
      });

      return {
        id: patio.id,
        nombre: patio.nombre,
        categoria: patio.categoria || 'PATIO',
        totalVehiculos: vehiculosPatio.length,
        marcas: marcasCounts,
        vehiculos: vehiculosPatio,
        estado: patio.estado,
        ubicacion: patio.ubicacion,
        capacidad: patio.capacidad || 0,
        estaVacio: vehiculosPatio.length === 0,
        porcentajeOcupacion: patio.capacidad > 0 ? (vehiculosPatio.length / patio.capacidad) * 100 : 0
      };
    });

    // Ordenar: sitios con vehículos primero
    todosLosSitios.sort((a, b) => {
      if (a.estaVacio && !b.estaVacio) return 1;
      if (!a.estaVacio && b.estaVacio) return -1;
      return a.nombre.localeCompare(b.nombre);
    });

    return {
      success: true,
      data: todosLosSitios
    };

  } catch (error) {
    console.error('Error al obtener todos los sitios del estado:', error);
    return {
      success: false,
      message: error.response?.data?.detail || 'Error al obtener sitios del estado',
      statusCode: error.response?.status,
      data: []
    };
  }
};

/**
 * Obtiene todos los estados con sus estadísticas de vehículos y sitios
 * 
 * Genera un resumen por estado:
 * - Total de vehículos detectados con RFID en patio
 * - Total de sitios
 * - Distribución por marca
 * - Lista de vehículos
 * 
 * Estados principales: Yucatán, Campeche, Quintana Roo
 * 
 * @async
 * @function fetchAllEstados
 * @param {Array} [vehiculosExistentes=[]] - Array de vehículos para procesar
 * @returns {Promise<Object>} Resultado con todos los estados
 * @property {boolean} success - true si fue exitoso
 * @property {Array} data - Array de estados con estadísticas
 */
export const fetchAllEstados = async (vehiculosExistentes = []) => {
  try {
    const patiosResult = await fetchPatios();
    if (!patiosResult.success) {
      return patiosResult;
    }

    const vehiculosData = vehiculosExistentes || [];

    // Filtrar solo vehículos con RFID en patio
    const vehiculosConRFID = vehiculosData.filter(vehiculo =>
      vehiculo.lectura_rfid &&
      vehiculo.lectura_rfid.patio_estado
    );

    const estadosMap = new Map();
    const estadosPrincipales = [];//esta lista puede servir para asignar estados "afuerza"

    // Inicializar estados principales
    estadosPrincipales.forEach(estadoNombre => {
      estadosMap.set(estadoNombre, {
        nombre: estadoNombre,
        totalVehiculos: 0,
        totalSitios: 0,
        marcas: {},
        vehiculos: [],
        sitios: new Set()
      });
    });

    // Construir set de estados válidos desde los patios registrados en BD
    const estadosValidos = new Set(patiosResult.data.map(p => p.estado).filter(Boolean));

    // Contar vehículos y sitios con vehículos por estado (solo estados válidos)
    vehiculosConRFID.forEach(vehiculo => {
      const estadoRFID = vehiculo.lectura_rfid.patio_estado;
      const sitioRFID = vehiculo.lectura_rfid.patio_nombre;

      if (!estadoRFID || !estadosValidos.has(estadoRFID)) return;

      if (!estadosMap.has(estadoRFID)) {
        estadosMap.set(estadoRFID, {
          nombre: estadoRFID,
          totalVehiculos: 0,
          totalSitios: 0,
          marcas: {},
          vehiculos: [],
          sitios: new Set()
        });
      }

      const estadoData = estadosMap.get(estadoRFID);
      estadoData.totalVehiculos += 1;
      estadoData.vehiculos.push(vehiculo);
      if (sitioRFID) estadoData.sitios.add(sitioRFID);

      const marca = vehiculo.Marca || 'DESCONOCIDA';
      estadoData.marcas[marca] = (estadoData.marcas[marca] || 0) + 1;
    });

    // Convertir Map a Array
    const todosLosEstados = Array.from(estadosMap.values()).map(estado => ({
      ...estado,
      totalSitios: estado.sitios.size,
      sitios: Array.from(estado.sitios),
      estaVacio: estado.totalVehiculos === 0
    }));

    // Ordenar: estados con vehículos primero
    todosLosEstados.sort((a, b) => {
      if (a.estaVacio && !b.estaVacio) return 1;
      if (!a.estaVacio && b.estaVacio) return -1;
      return a.nombre.localeCompare(b.nombre);
    });

    return {
      success: true,
      data: todosLosEstados
    };

  } catch (error) {
    console.error('Error al obtener todos los estados:', error);
    return {
      success: false,
      message: 'Error al obtener estados',
      data: []
    };
  }
};

/**
 * Crea un nuevo patio/sitio en el sistema
 * 
 * @async
 * @function createPatio
 * @param {Object} data - Datos del nuevo patio
 * @param {string} data.patioName - Nombre del patio
 * @param {number} data.capacidad - Capacidad de vehículos
 * @param {string} data.ubicacion - Dirección/ubicación
 * @param {string} data.estado - Estado (yucatan, campeche, quintana_roo)
 * @param {string} data.categoria - Categoría (PATIO, TALLER, etc.)
 * @param {string} [data.antena_rfid] - ID de antena RFID
 * @param {string} [data.ip_reader_rfid] - IP del lector RFID
 * @param {Array} [data.subPatios=[]] - Array de nombres de subsitios
 * @returns {Promise<Object>} Resultado de la creación
 */
export const createPatio = async (data) => {
  try {
    const payload = {
      nombre: data.patioName.trim(),
      capacidad: parseInt(data.capacidad) || 0,
      ubicacion: data.ubicacion.trim(),
      estado: data.estado,
      categoria: data.categoria,
      antena_rfid: data.antena_rfid || '',
      ip_reader_rfid: data.ip_reader_rfid || '',
      activo: true,
      subpatios_data: data.subPatios
        .filter(sp => sp.trim() !== '')
        .map(nombre => ({
          nombre: nombre.trim(),
          capacidad: 0,
          descripcion: ""
        }))
    };

    const response = await axios.post('/api/patios/', payload);

    return {
      success: true,
      data: response.data,
      message: 'Sitio creado exitosamente'
    };

  } catch (error) {
    console.error('Error completo:', error);

    if (error.response) {
      // Procesar errores del servidor
      let errorMessage = 'Error del servidor';
      if (error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(', ');
        } else {
          // Procesar errores de campo
          const fieldErrors = [];
          for (const [field, errors] of Object.entries(error.response.data)) {
            if (Array.isArray(errors)) {
              fieldErrors.push(`${field}: ${errors.join(', ')}`);
            } else if (typeof errors === 'string') {
              fieldErrors.push(`${field}: ${errors}`);
            }
          }
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('; ');
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        }
      }

      return {
        success: false,
        message: errorMessage,
        statusCode: error.response.status,
        details: error.response.data
      };

    } else if (error.request) {
      return {
        success: false,
        message: 'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
        details: 'Network error - no response received'
      };

    } else {
      return {
        success: false,
        message: `Error interno: ${error.message}`,
        details: error.message
      };
    }
  }
};

/**
 * Actualiza un patio existente
 * 
 * @async
 * @function updatePatio
 * @param {number} patioId - ID del patio a actualizar
 * @param {Object} data - Datos a actualizar (misma estructura que createPatio)
 * @returns {Promise<Object>} Resultado de la actualización
 */
export const updatePatio = async (patioId, data) => {
  try {
    const payload = {
      ...data,
      estado: data.estado,
      activo: data.activo !== undefined ? data.activo : true,
    };

    const response = await axios.put(`/api/patios/${patioId}/`, payload);

    return {
      success: true,
      data: response.data,
      message: 'Sitio actualizado exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar sitio:', error);

    return {
      success: false,
      message: error.response?.data?.detail || 'Error al actualizar sitio',
      statusCode: error.response?.status
    };
  }
};

/**
 * Elimina un patio del sistema
 * ADVERTENCIA: Esta operación puede ser irreversible
 * 
 * @async
 * @function deletePatio
 * @param {number} patioId - ID del patio a eliminar
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const deletePatio = async (patioId) => {
  try {
    await axios.delete(`/api/patios/${patioId}/`);
    return {
      success: true,
      message: 'Sitio eliminado exitosamente'
    };
  } catch (error) {
    console.error('Error al eliminar sitio:', error);
    return {
      success: false,
      message: error.response?.data?.error || error.response?.data?.detail || 'Error al eliminar sitio',
      statusCode: error.response?.status
    };
  }
};

/**
 * HOOK: usePatios
 * Hook personalizado para gestionar el estado de patios
 * 
 * @hook
 * @param {Object} config - Configuración del hook
 * @param {boolean} [config.autoRefresh=false] - Habilitar actualización automática
 * @param {number} [config.intervaloActualizacion=180000] - Intervalo en ms (default: 3 minutos)
 * @returns {Object} Estado del hook
 * @property {Array} patios - Array de patios
 * @property {boolean} loading - true durante carga
 * @property {string|null} error - Mensaje de error si hay
 * @property {Function} refetch - Función para recargar manualmente
 */
export const usePatios = ({ autoRefresh = false, intervaloActualizacion = 180000 } = {}) => {
  const [patios, setPatios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPatios = async () => {
    try {
      setLoading(true);
      const result = await fetchPatios();
      if (result.success) {
        const patiosArray = Array.isArray(result.data) ? result.data : [];
        setPatios(patiosArray);
        setError(null);
      } else {
        setPatios([]);
        setError(result.message);
      }
    } catch (err) {
      setError('Error al cargar sitios');
      setPatios([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatios();

    if (autoRefresh) {
      const interval = setInterval(loadPatios, intervaloActualizacion);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, intervaloActualizacion]);

  return { patios, loading, error, refetch: loadPatios };
};

/**
 * HOOK: useSubsitiosPatio
 * Hook personalizado para gestionar subsitios de un patio específico
 * 
 * @hook
 * @param {number|string} patioId - ID del patio
 * @param {Object} config - Configuración del hook
 * @param {boolean} [config.autoRefresh=false] - Habilitar actualización automática
 * @param {number} [config.intervaloActualizacion=180000] - Intervalo en ms
 * @returns {Object} Estado del hook
 * @property {Array} subsitios - Array de subsitios
 * @property {boolean} loading - true durante carga
 * @property {string|null} error - Mensaje de error
 * @property {Object|null} patioInfo - Información del patio padre
 * @property {Function} refetch - Función de recarga manual
 */
export const useSubsitiosPatio = (patioId, { autoRefresh = false, intervaloActualizacion = 180000 } = {}) => {
  const [subsitios, setSubsitios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patioInfo, setPatioInfo] = useState(null);

  const loadSubsitios = async () => {
    if (!patioId) {
      setSubsitios([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await fetchAllSubsitiosFromPatio(patioId);
      if (result.success) {
        setSubsitios(result.data);
        setPatioInfo(result.patio);
        setError(null);
      } else {
        setSubsitios([]);
        setError(result.message);
      }
    } catch (err) {
      setError('Error al cargar subsitios');
      setSubsitios([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubsitios();

    if (autoRefresh && patioId) {
      const interval = setInterval(loadSubsitios, intervaloActualizacion);
      return () => clearInterval(interval);
    }
  }, [patioId, autoRefresh, intervaloActualizacion]);

  return { subsitios, loading, error, patioInfo, refetch: loadSubsitios };
};
